import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { pluralise } from '@/shared/lib/format';
import type { SubscriberListRef } from '../api/subscribersApi';

type Filter = 'all' | 'topic' | 'client_book' | 'selected';
type Props = { anchor: HTMLElement | null; lists: SubscriberListRef[]; chosen: string[]; onClose: () => void; onApply: (ids: string[]) => void };

const GROUPS: [SubscriberListRef['kind'], string][] = [['topic', 'Topic lists'], ['client_book', 'Client books']];

/** Tick the lists a new subscriber joins: searchable, topic lists and client books apart, applied together. */
export function ListChooser({ anchor, lists, chosen, onClose, onApply }: Props) {
  const [picked, setPicked] = useState<string[]>(chosen);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const toggle = (id: string) => { setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); };
  const shown = lists.filter((l) =>
    l.name.toLowerCase().includes(q.trim().toLowerCase()) && (filter === 'all' || (filter === 'selected' ? picked.includes(l.id) : l.kind === filter)));
  const filters: [Filter, string][] = [['all', 'All'], ['topic', 'Topic lists'], ['client_book', 'Client books'], ['selected', `Selected · ${picked.length}`]];

  return (
    <Popover open={anchor !== null} anchorEl={anchor} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { width: 360, mt: 0.5, borderRadius: 1.5 } } }}>
      <Stack sx={{ gap: 1, p: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <TextField size="small" placeholder={`Search ${pluralise(lists.length, 'list')}`} value={q} onChange={(e) => { setQ(e.target.value); }}
          slotProps={{ htmlInput: { 'aria-label': 'Search lists' }, input: { startAdornment: <InputAdornment position="start"><Search size={14} aria-hidden /></InputAdornment> } }} />
        <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
          {filters.map(([f, label]) => <ButtonBase key={f} aria-pressed={filter === f} onClick={() => { setFilter(f); }} sx={chipSx(filter === f)}>{label}</ButtonBase>)}
        </Stack>
      </Stack>
      <Box sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
        {GROUPS.map(([kind, label]) => {
          const group = shown.filter((l) => l.kind === kind);
          if (group.length === 0) return null;
          return (
            <Box key={kind} role="group" aria-label={label}>
              <Typography variant="caption" component="p" sx={{ px: 1.5, pt: 1, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
              {group.map((l) => (
                <ButtonBase key={l.id} onClick={() => { toggle(l.id); }} sx={{ ...optionSx, bgcolor: picked.includes(l.id) ? 'surface.accentSoft' : 'transparent' }}>
                  <Checkbox size="small" checked={picked.includes(l.id)} tabIndex={-1} slotProps={{ input: { 'aria-label': l.name } }} sx={{ p: 0.25 }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{l.name}</Typography>
                    {l.description ? <Typography variant="caption" noWrap component="p" sx={{ color: 'text.secondary' }}>{l.description}</Typography> : null}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>{pluralise(l.optedInCount, 'number')}</Typography>
                </ButtonBase>
              ))}
            </Box>
          );
        })}
        {shown.length === 0 ? <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>No list matches.</Typography> : null}
      </Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 'auto' }}>{picked.length} selected</Typography>
        <Button size="small" onClick={() => { setPicked([]); }} disabled={picked.length === 0}>Clear</Button>
        <Button size="small" variant="contained" disabled={picked.length === 0} onClick={() => { onApply(picked); }}>
          Add {pluralise(picked.length, 'list')}
        </Button>
      </Stack>
    </Popover>
  );
}

const chipSx = (isOn: boolean) => ({
  px: 1.25,
  height: 26,
  borderRadius: 5,
  border: 1,
  borderColor: isOn ? 'text.primary' : 'divider',
  bgcolor: isOn ? 'text.primary' : 'background.paper',
  color: isOn ? 'background.paper' : 'text.primary',
  fontSize: '0.75rem',
  fontWeight: 500,
});

const optionSx = {
  width: '100%',
  gap: 1,
  px: 1.25,
  py: 0.75,
  '&:hover': { bgcolor: 'surface.sidebar' },
} as const;
